/**
 * HFDS — Headzflex Design System
 * Integration: Ella Native Cart Drawer Bridge
 * Version: 1.1.0
 *
 * Source of truth:
 * - Uses the `sections` returned by Shopify in the SAME cart/add.js
 *   response that added the product.
 *
 * This avoids:
 * - A second request returning stale empty-cart HTML.
 * - Timing retries.
 * - Competing cart refresh events.
 * - Incorrect validation of Ella's section markup.
 */

(() => {
  "use strict";

  const VERSION = "1.1.0";
  const EVENT_NAME = "hfds:cart:item-added";
  const DRAWER_SELECTOR = "cart-drawer";
  const REQUIRED_SECTION = "cart-drawer";

  const state = {
    initialized: false,
    rendering: false,
    renderCount: 0,
    failureCount: 0,
    lastResult: null
  };

  function getDrawer() {
    const drawer =
      document.querySelector(
        DRAWER_SELECTOR
      );

    return drawer instanceof HTMLElement
      ? drawer
      : null;
  }

  function getSections(detail) {
    const sections =
      detail?.sections ||
      detail?.lineItem?.sections;

    if (
      !sections ||
      typeof sections !== "object"
    ) {
      return null;
    }

    if (
      typeof sections[REQUIRED_SECTION] !==
        "string" ||
      !sections[REQUIRED_SECTION]
    ) {
      return null;
    }

    return sections;
  }

  function getItemCount(detail) {
    const count =
      Number(detail?.cart?.item_count);

    return Number.isFinite(count)
      ? count
      : 1;
  }

  function synchronizeEmptyState(
    drawer,
    isEmpty
  ) {
    drawer.classList.toggle(
      "is-empty",
      isEmpty
    );

    drawer
      .querySelector(".drawer__inner")
      ?.classList.toggle(
        "is-empty",
        isEmpty
      );
  }

  function getLineItemId(detail) {
    return (
      detail?.lineItem?.key ||
      detail?.lineItem?.id ||
      detail?.variant?.id ||
      null
    );
  }

  function render(detail = {}) {
    if (state.rendering) {
      return state.lastResult;
    }

    const drawer = getDrawer();
    const sections = getSections(detail);

    if (!drawer) {
      const result = {
        success: false,
        reason: "cart-drawer-not-found"
      };

      state.failureCount += 1;
      state.lastResult = result;

      console.error(
        "[HFDS Ella Cart Bridge] cart-drawer was not found."
      );

      return result;
    }

    if (
      typeof drawer.renderContents !==
        "function"
    ) {
      const result = {
        success: false,
        reason:
          "renderContents-not-available"
      };

      state.failureCount += 1;
      state.lastResult = result;

      console.error(
        "[HFDS Ella Cart Bridge] Ella renderContents() is unavailable."
      );

      return result;
    }

    if (!sections) {
      const result = {
        success: false,
        reason:
          "bundled-sections-missing"
      };

      state.failureCount += 1;
      state.lastResult = result;

      console.error(
        "[HFDS Ella Cart Bridge] The cart/add.js response did not include cart-drawer sections. Install hfds-purchase-engine.js v1.0.2 together with this bridge."
      );

      return result;
    }

    state.rendering = true;

    try {
      const itemCount =
        getItemCount(detail);

      const isEmpty =
        itemCount === 0;

      /*
       * Ella's open() checks the OUTER custom element. It must be
       * synchronized before renderContents() schedules open().
       */
      synchronizeEmptyState(
        drawer,
        isEmpty
      );

      if (
        document.activeElement instanceof
          HTMLElement &&
        typeof drawer.setActiveElement ===
          "function"
      ) {
        drawer.setActiveElement(
          document.activeElement
        );
      }

      drawer.renderContents({
        id: getLineItemId(detail),
        sections
      });

      /*
       * renderContents() replaces #CartDrawer content but does not
       * replace the outer custom element. Reapply the state after the
       * internal markup has been inserted.
       */
      window.setTimeout(() => {
        synchronizeEmptyState(
          drawer,
          isEmpty
        );
      }, 0);

      window.setTimeout(() => {
        synchronizeEmptyState(
          drawer,
          isEmpty
        );
      }, 120);

      window.HFDSPurchaseEngine
        ?.cartConfirmation
        ?.hide?.();

      const result = {
        success: true,
        itemCount,
        emptyState: isEmpty,
        sectionIds:
          Object.keys(sections)
      };

      state.renderCount += 1;
      state.lastResult = result;

      document.dispatchEvent(
        new CustomEvent(
          "hfds:ella-cart:rendered",
          {
            bubbles: true,
            detail: result
          }
        )
      );

      return result;
    } catch (error) {
      const result = {
        success: false,
        reason: "render-failed",
        message:
          error instanceof Error
            ? error.message
            : String(error)
      };

      state.failureCount += 1;
      state.lastResult = result;

      console.error(
        "[HFDS Ella Cart Bridge] Native drawer render failed:",
        error
      );

      return result;
    } finally {
      state.rendering = false;
    }
  }

  function handleItemAdded(event) {
    render(event?.detail || {});
  }

  function initialize() {
    if (state.initialized) {
      return;
    }

    document.addEventListener(
      EVENT_NAME,
      handleItemAdded
    );

    state.initialized = true;
  }

  function destroy() {
    document.removeEventListener(
      EVENT_NAME,
      handleItemAdded
    );

    state.initialized = false;
  }

  function getStatus() {
    const drawer = getDrawer();

    return {
      version: VERSION,
      initialized: state.initialized,
      rendering: state.rendering,
      renderCount: state.renderCount,
      failureCount: state.failureCount,
      drawerFound: Boolean(drawer),
      drawerIsEmpty:
        drawer?.classList.contains(
          "is-empty"
        ) ?? null,
      lastResult: state.lastResult
    };
  }

  window.HFDSEllaCartBridge =
    Object.freeze({
      version: VERSION,
      initialize,
      destroy,
      render,
      getStatus
    });

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();
